import type { ReactNode } from "react";

import {
  IgCoreTextColors,
  IgGrayPrimitives,
} from "@intelligems/ig-design-tokens/constants";
import Box from "@mui/material/Box";

interface MessageBodyProps {
  children: ReactNode;
  /** A surface's own classes (`slotProps.userMessage` / `assistantMessage`), merged last. */
  className?: string;
  compact?: boolean;
}

/**
 * Clips oversized children (wide images, long code) at the content edge and
 * breaks unbreakable strings (URLs, hashes), so neither pushes the message
 * past its column.
 */
const Clip = ({ children }: { children: ReactNode }) => (
  <div className="min-w-0 overflow-hidden [overflow-wrap:anywhere]">
    {children}
  </div>
);

/**
 * Figma "Atlas / User message" (516:3146): a right-aligned bubble on
 * #ebebeb (the gray/07 primitive — no semantic fill carries it), rounded 16px
 * except the 4px corner under the sender, padded 12px/16px, holding Body 2
 * regular in Text/Core/Primary.
 */
export const UserMessageBody = ({
  children,
  className,
  compact,
}: MessageBodyProps) => (
  <Box
    className={className}
    sx={{
      backgroundColor: IgGrayPrimitives[600],
      borderRadius: "16px 16px 4px 16px",
      color: IgCoreTextColors.primary,
      fontSize: 12,
      lineHeight: "18px",
      maxWidth: compact ? "92%" : "80%",
      minWidth: 0,
      px: compact ? 3 : 4,
      py: compact ? 2 : 3,
    }}
  >
    <Clip>{children}</Clip>
  </Box>
);

/**
 * Figma "chat-panel" › Atlas reply (15781:10903): no bubble — plain Body 2
 * regular in Text/Core/Secondary across the full column, with no fill, radius
 * or padding of its own.
 */
export const AssistantMessageBody = ({
  children,
  className,
}: MessageBodyProps) => (
  <Box
    className={className}
    sx={{
      color: IgCoreTextColors.secondary,
      fontSize: 12,
      lineHeight: "18px",
      minWidth: 0,
      width: "100%",
    }}
  >
    <Clip>{children}</Clip>
  </Box>
);
